import { supabase } from "@/lib/supabaseClient";
const API_BASE_URL = process.env.REACT_APP_API_URL;

// 🟢 Helper: Get auth header
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// 🧠 NEW: Timeout helper (4 seconds)
const timeoutFetch = (url, options = {}, timeout = 4000) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), timeout)
    )
  ]);
};

// ==========================================================
// ✅ normalizeCategory: converts various category formats to a consistent one
const normalizeCategory = (cat) => {
  if (!cat || typeof cat !== "string") return "uncategorized";

  return cat
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_-]/g, "")
    .replace(/[\s_-]+/g, "_")
    .replace(/^_+|_+$/g, "") || "uncategorized";
};

// 🧠 Improved safeFetch: timeout + background retry
const safeFetch = async (url, options = {}) => {
  try {
    // Try backend but fail if it exceeds 4 seconds
    const response = await timeoutFetch(url, options);

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    console.warn(`⚠️ Backend slow/unreachable → switching to Supabase: ${url}`);

    // 🔄 Background retry (does NOT block UI)
    fetch(url, options)
      .then(async (res) => {
        if (res.ok) {
          const freshData = await res.json();
          console.log("🔄 Background backend request succeeded later:", freshData);
        }
      })
      .catch(() => {});

    return null; // tell the caller to use Supabase fallback
  }
};


// ==========================================================
// ✅ Fetch all products
export const fetchProducts = async () => {
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error("❌ Error fetching products:", err.message);
    return [];
  }
};

export const fetchGroupedProducts = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params);

    const res = await safeFetch(
      `${API_BASE_URL}/products?${queryParams.toString()}`,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
      }
    );

    return res?.groups || {};
  } catch (err) {
    console.error("❌ Grouped fetch error:", err.message);
    return {};
  }
};

// ==========================================================
// ✅ Fetch paginated products (optional maxPrice, fallback to Supabase)
export const fetchProductsPaginated = async ({
  page = 1,
  limit = 20,
  category = "",
  maxPrice,
}) => {
  try {
    const queryParams = new URLSearchParams({
      page,
      limit,
      category: category ? normalizeCategory(category) : "",
      maxPrice: maxPrice ?? "",
    });

    // 🔥 Try backend FIRST (grouped)
    const res = await safeFetch(
      `${API_BASE_URL}/products?${queryParams.toString()}`,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
      }
    );

    // ✅ If backend works → return grouped directly
    if (res && res.groups) {
      return {
        groups: res.groups,
        total: res.total || 0,
        page,
      };
    }

    // -----------------------------------
    // 🟡 Supabase fallback (group manually)
    // -----------------------------------
    let query = supabase.from("products").select("*", { count: "exact" });

    if (category) {
      query = query.eq("category", normalizeCategory(category));
    }

    if (maxPrice != null) {
      query = query.lte("price", maxPrice);
    }

    const from = (page - 1) * limit;
    const to = from + limit - 1;

    const { data, error, count } = await query
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) throw error;

    // 🔥 GROUP fallback data
    const groups = {};

    (data || []).forEach((p) => {
      const key = normalizeCategory(p.category);

      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });

    return {
      groups,
      total: count || 0,
      page,
    };

  } catch (err) {
    console.error("❌ Paginated fetch error:", err.message);
    return { groups: {}, page, total: 0 };
  }
};

// ==========================================================
// ✅ Fetch categories
export const fetchCategories = async () => {
  try {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("name", { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error("❌ Error fetching categories:", err.message);
    return [];
  }
};

// ==========================================================
// ✅ Fetch single product
export const fetchProductById = async (productId) => {
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("id", productId)
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    console.error("❌ Error fetching product:", err.message);
    return null;
  }
};

// ==========================================================
// ✅ Fetch products by category
export const fetchProductsByCategory = async (category) => {
  try {
    const normalized = normalizeCategory(category);

    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("category", normalized)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error("❌ Category fetch error:", err.message);
    return [];
  }
};

// ==========================================================
// ✅ Add new product
// ==========================================================
export const addProduct = async (productData) => {
  const token = localStorage.getItem("token");

  let formData;
  if (productData instanceof FormData) {
    formData = productData;
  } else {
    formData = new FormData();
    if (productData.images instanceof FileList) {
      productData.images = Array.from(productData.images);
    }

    Object.keys(productData).forEach((key) => {
      if (key === "images" && Array.isArray(productData.images)) {
        productData.images.forEach((img) => formData.append("images", img));
      } else if (productData[key] !== undefined && productData[key] !== null) {
        formData.append(key, productData[key]);
      }
    });
  }

  try {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Failed to add product");
    }

    return await response.json();
  } catch (error) {
    console.warn("⚠️ Backend failed — trying Supabase...");
    const { data, error: supaErr } = await supabase
      .from("products")
      .insert([productData])
      .select()
      .single();

    if (supaErr) console.error("❌ Supabase insert error:", supaErr.message);
    return data || null;
  }
};

// ==========================================================
// ✅ Update product
// ==========================================================
export const updateProduct = async (id, productData) => {
  try {
    const isFormData = productData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: "PUT",
      headers: isFormData
        ? getAuthHeaders()
        : { "Content-Type": "application/json", ...getAuthHeaders() },
      body: isFormData ? productData : JSON.stringify(productData),
      credentials: "include",
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Failed to update product`);
    }

    return await response.json();
  } catch (error) {
    console.warn("⚠️ Backend update failed — trying Supabase...");
    const { data, error: supaErr } = await supabase
      .from("products")
      .update(productData)
      .eq("id", id)
      .select()
      .single();

    if (supaErr) console.error("❌ Supabase update error:", supaErr.message);
    return data || null;
  }
};

// ==========================================================
// ✅ Delete product
// ==========================================================
export const deleteProduct = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
      credentials: "include",
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Failed to delete product");
    }

    return { success: true };
  } catch (error) {
    console.warn("⚠️ Backend delete failed — trying Supabase...");
    const { error: supaErr } = await supabase.from("products").delete().eq("id", id);
    if (supaErr) console.error("❌ Supabase delete error:", supaErr.message);
    return { success: !supaErr };
  }
};

// ==========================================================
// ⭐ Product Reviews
// ==========================================================
export const fetchProductReviews = async (productId) => {
  try {
    const { data, error } = await supabase
      .from("product_reviews")
      .select("id, user_name, rating, content, created_at")
      .eq("product_id", productId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    const reviews = data || [];
    const avg =
      reviews.length > 0
        ? reviews.reduce((sum, r) => sum + Number(r.rating), 0) /
          reviews.length
        : 0;

    return {
      reviews,
      average_rating: Number(avg.toFixed(1)),
    };
  } catch (err) {
    console.error("❌ Error fetching reviews:", err.message);
    return { reviews: [], average_rating: 0 };
  }
};


export const addProductReview = async (productId, content, rating = 5) => {
  try {
    const { data, error } = await supabase
      .from("product_reviews")
      .insert([
        {
          product_id: productId,
          content,
          rating,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    console.error("❌ Error adding review:", err.message);
    return null;
  }
};


export const fetchTopRatedProducts = async (limit = 10) => {
  const { data, error } = await supabase
    .from("top_rated_products")
    .select("*")
    .limit(limit);

  if (error) {
    console.error(error.message);
    return [];
  }
  return data;
};

export const fetchMostReviewedProducts = async (limit = 10) => {
  const { data, error } = await supabase
    .from("most_reviewed_products")
    .select("*")
    .limit(limit);

  if (error) {
    console.error(error.message);
    return [];
  }
  return data;
};


// ==========================================================
// ✅ Import supplier product (admin only)
// ==========================================================
export const importProduct = async (productData) => {
  try {
    // ✅ Convert numeric fields automatically
    const numericFields = ["price", "variants"];
    numericFields.forEach((key) => {
      if (productData[key] != null) productData[key] = Number(productData[key]);
    });

    // ✅ Handle images: FormData if there are files
    let body;
    let headers = getAuthHeaders();

    if (productData.images && productData.images.some((img) => img instanceof File)) {
      body = new FormData();
      Object.keys(productData).forEach((key) => {
        if (key === "images") {
          productData.images.forEach((img) => body.append("images", img));
        } else if (productData[key] != null) {
          body.append(key, productData[key]);
        }
      });
      // Don't set Content-Type; browser will handle multipart/form-data
    } else {
      body = JSON.stringify(productData);
      headers = { "Content-Type": "application/json", ...headers };
    }

    const res = await fetch(`${API_BASE_URL}/products/import`, {
      method: "POST",
      headers,
      body,
      credentials: "include",
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to import product");
    }

    return await res.json();
  } catch (error) {
    console.warn("⚠️ Backend import failed — trying Supabase fallback...", error.message);

    // 🔄 Supabase fallback: insert into 'products'
    try {
      const { data, error: supaErr } = await supabase
        .from("products")
        .insert([productData])
        .select()
        .single();

      if (supaErr) throw new Error(supaErr.message);
      return data;
    } catch (supaError) {
      console.error("❌ Supabase import failed:", supaError.message);
      return null;
    }
  }
};

// ==========================================================
// 💖 Wishlist Integration (Backend → Supabase fallback)
// ==========================================================
export const getWishlist = async () => {
  try {
    const res = await safeFetch(`${API_BASE_URL}/products/wishlist`, {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      credentials: "include",
    });

    if (!res) {
      const { data, error } = await supabase.from("wishlist").select("*");
      if (error) throw new Error(error.message);
      return data || [];
    }

    return res;
  } catch (error) {
    console.error("❌ Error fetching wishlist:", error.message);
    return [];
  }
};

export const addToWishlist = async (productId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/products/wishlist`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ productId }),
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to add to wishlist");
    return await res.json();
  } catch (error) {
    console.warn("⚠️ Backend addToWishlist failed — trying Supabase...");
    const { data, error: supaErr } = await supabase
      .from("wishlist")
      .insert([{ product_id: productId }])
      .select()
      .single();

    if (supaErr) console.error("❌ Supabase wishlist error:", supaErr.message);
    return data || null;
  }
};

export const removeFromWishlist = async (productId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/products/wishlist/${productId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to remove from wishlist");
    return await res.json();
  } catch (error) {
    console.warn("⚠️ Backend removeFromWishlist failed — trying Supabase...");
    const { error: supaErr } = await supabase
      .from("wishlist")
      .delete()
      .eq("product_id", productId);
    if (supaErr) console.error("❌ Supabase wishlist delete error:", supaErr.message);
    return { success: !supaErr };
  }
};

export const checkIfWishlisted = async (productId) => {
  try {
    const res = await safeFetch(`${API_BASE_URL}/products/wishlist/check/${productId}`, {
      headers: getAuthHeaders(),
      credentials: "include",
    });

    if (!res) {
      const { data, error } = await supabase
        .from("wishlist")
        .select("*")
        .eq("product_id", productId)
        .maybeSingle();

      if (error) throw new Error(error.message);
      return !!data;
    }

    return res.isWishlisted || false;
  } catch (error) {
    console.error("❌ Error checking wishlist:", error.message);
    return false;
  }
};


// ==========================================================
// 🧠 Fetch product recommendations (backend → Supabase fallback)
// ==========================================================
export const fetchRecommendations = async (limit = 10) => {
  try {
    // ✅ Use safeFetch to call backend endpoint
    const data = await safeFetch(`${API_BASE_URL}/products/recommendations/list?limit=${limit}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    });

    // ✅ If backend fails, fallback to Supabase
    if (!data) {
      const { data: fallback, error: supaErr } = await supabase
        .from("products")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);

      if (supaErr) console.error("❌ Supabase fallback failed:", supaErr.message);
      return fallback || [];
    }

    // ✅ Handle consistent API responses
    return Array.isArray(data)
      ? data
      : data.recommendations || data.products || data.data || [];
  } catch (error) {
    console.warn("⚠️ Recommendation fetch failed:", error.message);
    return [];
  }
};